//#region Imports Packages and Libs

import { Component, Prop, Vue, Watch } from "vue-facing-decorator";
import { __vueEvent } from "../../constant";
import { EVENTS, FORM_COMPONENT_TYPE, STORE_MUTATION } from "../../enums";
import { getUniqId } from "../../helpers";
import { store } from "../../store/store";
import type { FormItemComponent } from "../../types/forms";
import { Util } from "../../util";

//#endregion

//#region Define the component in class-style

@Component
export default class NumberSpinner extends Vue {
	//#region Class properties will be component data

	@Prop() component?: FormItemComponent;
	@Prop() messageId?: string;
	fieldId = getUniqId();
	fieldValue: string = "0"; // Default value. We need to update this logic for set/restoring the value from the store.
	timeoutId: ReturnType<typeof setTimeout> | undefined;

	//#endregion

	//#region Computed Properties

	/**
	 * This computed prop is used to get the prefix class name.
	 */
	get prefixClassName() {
		return this.$store.state.prefixClassName || "";
	}

	/**
	 * A computed property to get the minimum value of the spinner.
	 */
	get minValue() {
		return this.component && this.component.minValue ? this.component.minValue : 0;
	}

	/**
	 * A computed property to get the maximum value of the spinner.
	 */
	get maxValue() {
		return this.component && this.component.maxValue ? this.component.maxValue : 100;
	}

	/**
	 * A Computed property to check if the decrease button should be disabled.
	 */
	get isDecreaseDisabled(): boolean {
		let currentValue = Number(this.fieldValue) || 0;
		return currentValue <= this.minValue;
	}

	/**
	 * A Computed property to check if the increase button should be disabled.
	 */
	get isIncreaseDisabled(): boolean {
		let currentValue = Number(this.fieldValue) || 0;
		return currentValue >= this.maxValue;
	}

	/**
	 * This method is used to create a computed property that returns the font style for the label text.
	 */
	get computedFontStyle() {
		return Util.getLabelFontStyle(this.component, FORM_COMPONENT_TYPE.NumberSpinner);
	}

	/**
	 * This method is used to create a computed property that returns the layout of the component.
	 */
	get isHorizontal() {
		return this.component && this.component.layout === "horizontal";
	}

	/**
	 * This method is used to validate the field value.
	 */
	get isFieldValid() {
		try {
			// Check if the component is required
			if (!this.component || !this.component.isRequired) {
				return true; // If not required, return true
			}

			// If the item is required, validate for non-null and non-empty values
			if (Util.isNullOrEmpty(this.fieldValue)) {
				return false;
			}

			// Additional validation for 'number_spinner' type to check value range
			const value = Number(this.fieldValue);
			if (isNaN(value) || value < this.minValue || value > this.maxValue) {
				return false;
			}

			return true; // Passes validation
		} catch (ex) {
			return false;
		}
	}

	//#endregion

	//#region Modal Lifecycle Hooks

	mounted() {
		this.setSpinnerOptions();
	}

	//#endregion

	//#region Main Methods

	/**
	 * This method is used to set the spinner initial value when the component is mounted.
	 */
	setSpinnerOptions() {
		try {
			if (this.component && this.component.value !== undefined && !Util.isNullOrEmpty(this.component.value)) {
				this.fieldValue = String(this.component.value);
			}
		} catch (ex) {
			//console.error(ex);
		}
	}

	decreaseValue() {
		try {
			let currentValue = Number(this.fieldValue) || 0;
			if (currentValue > this.minValue) {
				currentValue--;
			}
			this.fieldValue = currentValue.toString();
		} catch (ex) {
			//console.error(ex);
		}
	}

	increaseValue() {
		try {
			let currentValue = Number(this.fieldValue) || 0;
			if (currentValue < this.maxValue) {
				currentValue++;
			}
			this.fieldValue = currentValue.toString();
		} catch (ex) {
			//console.error(ex);
		}
	}

	onSpinnerInput() {
		try {
			const input = <HTMLInputElement>this.$refs.input;
			if (!input) return;

			// If the user has manually cleared the input, keep it empty.
			if (Util.isNullOrEmpty(input.value)) return;
			let currentValue = Number(input.value) || 0;

			// Reset to min/max if value is outside the range
			if (currentValue < this.minValue) {
				currentValue = this.minValue;
			} else if (currentValue > this.maxValue) {
				currentValue = this.maxValue;
			}

			this.fieldValue = currentValue.toString();
		} catch (ex) {
			//console.error(ex);
		}
	}

	@Watch("fieldValue", { immediate: true })
	onPropertyChanged(value: string) {
		try {
			// Clear any previous timer
			if (this.timeoutId) {
				clearTimeout(this.timeoutId);
			}

			// Set a new timer to run the logic after 500ms
			this.timeoutId = setTimeout(() => {
				// Emit an event to notify the parent about the value change
				store.commit(STORE_MUTATION.SetFormItemChange, {
					messageId: this.messageId,
					name: this.component.name,
					value: value,
				});

				// Emit validation-change event
				__vueEvent.emit(EVENTS.ValidationChange);
			}, 500);
		} catch (ex) {
			console.log("[form_input.ts] An error has occurred \n-> Inside onPropertyChanged -> Error: ", ex);
		}
	}

	//#endregion
}

//#endregion
